import { useProgressiveOnboarding } from 'providers/ProgressiveOnboarding/ProgressiveOnboardingProvider';
import type { Guide } from 'providers/ProgressiveOnboarding/types';
import { Dispatch, useMemo } from 'react';

export interface LaunchpadGuideInfo {
  guide: Guide;
  completedSteps: number;
  totalSteps: number;
  currentStepTitle: string | undefined;
  subtitle: string;
  isComplete: boolean;
}

export interface UseLaunchpadGuidesResult {
  incompleteGuides: LaunchpadGuideInfo[];
  completedGuides: LaunchpadGuideInfo[];
  sidebarGuide: LaunchpadGuideInfo | undefined;
  isLoading: boolean;
  error: unknown;
  setActiveGuide: Dispatch<Guide | undefined>;
}

const hasValidContent = (guide: Guide) =>
  Boolean(guide.title && guide.steps.length > 0);

const byMostRecentlyTouched = (
  a: LaunchpadGuideInfo,
  b: LaunchpadGuideInfo,
) => {
  const aTime = a.guide.lastCompletedAt ?? '';
  const bTime = b.guide.lastCompletedAt ?? '';
  if (aTime > bTime) return -1;
  if (aTime < bTime) return 1;
  return 0;
};

const toGuideInfo = (guide: Guide): LaunchpadGuideInfo => {
  const completedSteps = guide.steps.filter(
    (s) => s.state === 'complete',
  ).length;
  const totalSteps = guide.steps.length;
  const isComplete = completedSteps === totalSteps;
  const currentStepTitle = guide.steps.find(
    (s) => s.state === 'incomplete',
  )?.title;

  return {
    guide,
    completedSteps,
    totalSteps,
    currentStepTitle,
    subtitle: isComplete
      ? `Completed ${guide.title}.`
      : currentStepTitle ?? guide.title,
    isComplete,
  };
};

export const useLaunchpadGuides = (): UseLaunchpadGuidesResult => {
  const { allGuides, isLoading, error, setActiveGuide, dismissedGuideKeys } =
    useProgressiveOnboarding();

  const guideInfos = useMemo(
    () => allGuides.filter(hasValidContent).map(toGuideInfo),
    [allGuides],
  );

  const incompleteGuides = useMemo(
    () => guideInfos.filter((g) => !g.isComplete).sort(byMostRecentlyTouched),
    [guideInfos],
  );

  const completedGuides = useMemo(
    () => guideInfos.filter((g) => g.isComplete),
    [guideInfos],
  );

  const sidebarGuide = useMemo(
    () => incompleteGuides.find((g) => !dismissedGuideKeys.has(g.guide.key)),
    [incompleteGuides, dismissedGuideKeys],
  );

  return {
    incompleteGuides,
    completedGuides,
    sidebarGuide,
    isLoading,
    error,
    setActiveGuide,
  };
};
